import React from "react";
import { GroupBase, SelectInstance } from "react-select";
import {
  AsyncPaginate as AsyncPaginateComponent,
  AsyncPaginateProps,
} from "react-select-async-paginate";
import {
  OptionType,
  Control,
  DropdownIndicator,
  ClearIndicator,
  MultiValue,
  MultiValueRemove,
  Option,
  Menu,
  SelectInputClassNameOptions,
  useStyles,
  useClassNames,
  defaultPlaceholder,
} from "./select-input";
import { ApiError } from "@/client/extend";
import {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  useOptionsMutationLoader,
  useOptionsQueryLoader,
} from "./options-cache";
import { useMenuPortal } from "./menu-portal-context";
import { firstNotUndefined } from "@/lib/utils";
import { combineRefs } from "@/hooks/combine-refs";
import { CopyPasteProps, useCopyPaste } from "./copy-paste";
import { useSelectInputCache } from "@/state/select-input-cache-context";

export type SelectInputAsyncPaginateProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  Additional,
> = AsyncPaginateProps<Option, Group, Additional, IsMulti> & {
  classNamesOptions?: SelectInputClassNameOptions;
} & CopyPasteProps<Value, Option, IsMulti, Group>;

const SelectInputAsyncPaginate_ = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  Additional,
>(
  props: SelectInputAsyncPaginateProps<
    Value,
    Option,
    IsMulti,
    Group,
    Additional
  > & { ref?: React.Ref<SelectInstance<Option, IsMulti, Group>> },
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  const {
    styles: _styles,
    classNames: _classNames,
    classNamesOptions,
    components,
    menuPortalTarget,
    placeholder = defaultPlaceholder,
    closeMenuOnSelect = !props.isMulti,
    copyPaste: copyPasteEnable,
    ...rest
  } = props;
  const instanceId = React.useId();

  const styles = useStyles({
    styles: _styles,
    options: classNamesOptions,
  });

  const classNames = useClassNames({
    classNames: _classNames,
    options: classNamesOptions,
  });

  const {
    ref: copyPasteRef,
    copy: _copy,
    storeOptions: _storeOptions,
    ...copyPaste
  } = useCopyPaste<Value, Option, IsMulti, Group>(props);

  const _menuPortalTarget = useMenuPortal();

  return (
    <AsyncPaginateComponent
      {...copyPaste}
      selectRef={combineRefs(ref, copyPasteRef)}
      instanceId={instanceId}
      unstyled
      components={{
        Control,
        DropdownIndicator,
        ClearIndicator,
        MultiValue,
        MultiValueRemove,
        Option,
        Menu,
        ...copyPaste.components,
        ...components,
      }}
      styles={styles}
      classNames={classNames}
      menuPortalTarget={firstNotUndefined(
        menuPortalTarget,
        _menuPortalTarget,
        document.body,
      )}
      placeholder={placeholder}
      closeMenuOnSelect={closeMenuOnSelect}
      {...rest}
    />
  );
};

export const SelectInputAsyncPaginate = React.forwardRef(
  SelectInputAsyncPaginate_,
) as typeof SelectInputAsyncPaginate_;

export default SelectInputAsyncPaginate;

export type SelectInputAsyncPaginateMutationProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData = any,
  TError = ApiError,
  TVariables = void,
  TContext = unknown,
> = Omit<
  SelectInputAsyncPaginateProps<
    Value,
    Option,
    IsMulti,
    Group,
    { page: number }
  >,
  "loadOptions"
> & {
  mutation: UseMutationResult<TData, TError, TVariables, TContext>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables | null;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
};

const SelectInputAsyncPaginateMutationBase = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData = any,
  TError = ApiError,
  TVariables = void,
  TContext = unknown,
>(
  props: SelectInputAsyncPaginateMutationProps<
    Value,
    Option,
    IsMulti,
    Group,
    TData,
    TError,
    TVariables,
    TContext
  >,
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  return (
    <SelectInputAsyncPaginate
      ref={ref}
      {...props}
      loadOptions={useOptionsMutationLoader<
        Value,
        Option,
        IsMulti,
        Group,
        TData,
        TError,
        TVariables,
        TContext
      >(props)}
    />
  );
};

export const SelectInputAsyncPaginateMutation = React.forwardRef(
  SelectInputAsyncPaginateMutationBase,
) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData = any,
  TError = ApiError,
  TVariables = void,
  TContext = unknown,
>(
  p: SelectInputAsyncPaginateMutationProps<
    Value,
    Option,
    IsMulti,
    Group,
    TData,
    TError,
    TVariables,
    TContext
  >,
) => React.ReactElement;

export type SelectInputAsyncPaginateQueryProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<
  SelectInputAsyncPaginateProps<
    Value,
    Option,
    IsMulti,
    Group,
    { page: number }
  >,
  "loadOptions"
> & {
  queryCreator: (
    argument: TVariables,
  ) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables | null;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
  prefixOptions?: Option[];
};

export const SelectInputAsyncPaginateQueryBase = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  props: SelectInputAsyncPaginateQueryProps<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  >,
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  const { cacheUniq } = useSelectInputCache();
  const loadOptions = useOptionsQueryLoader<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  >(props);
  return (
    <SelectInputAsyncPaginate
      {...props}
      // After the spread, or a caller's own cacheUniqs replaces this array and the select stops
      // hearing the app-wide cache bust (account switch, access denied).
      cacheUniqs={[cacheUniq, ...(props.cacheUniqs ?? [])]}
      ref={ref as any}
      loadOptions={loadOptions}
    />
  );
};

export const SelectInputAsyncPaginateQuery = React.forwardRef(
  SelectInputAsyncPaginateQueryBase,
) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  p: SelectInputAsyncPaginateQueryProps<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  > & {
    ref?: React.Ref<React.LegacyRef<SelectInstance<Option, IsMulti, Group>>>;
  },
) => React.ReactElement;
